
import { useState } from 'react';
import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostActions } from './PostActions';
import { PostComments } from './PostComments';
import { CommentsToggle } from './CommentsToggle';
import { LinkedStartup } from './LinkedStartup';
import { useLinkedStartup } from '@/hooks/useLinkedStartup';
import { Card } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';

interface PostProps {
  post: {
    id: string;
    content: string;
    user_id: string;
    created_at: string;
    media_url?: string | null;
    media_type?: string | null;
    attachment_url?: string | null;
    attachment_type?: string | null;
    attachment_name?: string | null;
    profiles: {
      username: string;
      full_name: string | null;
      avatar_url: string | null;
      is_pro?: boolean;
    };
    comments?: Array<{
      id: string;
      content: string;
      created_at: string;
      user_id: string;
      parent_id?: string | null;
      profiles: {
        username: string;
        avatar_url: string | null;
      };
    }>;
  };
  isDetailView?: boolean;
  currentUserId?: string;
}

export const Post = ({ post, isDetailView = false, currentUserId }: PostProps) => {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [commentCount, setCommentCount] = useState(post.comments?.length || 0);
  
  const { linkedStartup } = useLinkedStartup(post.content);

  const formatUsername = (username: string) => {
    return username.toLowerCase().replace(/\s+/g, '_');
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on links or buttons
    if (
      (e.target as HTMLElement).tagName === 'A' ||
      (e.target as HTMLElement).tagName === 'BUTTON' ||
      (e.target as HTMLElement).closest('a') ||
      (e.target as HTMLElement).closest('button')
    ) {
      return;
    }

    if (!isDetailView) {
      navigate(`/feed/post/${post.id}`);
    }
  };

  return (
    <Card 
      className="bg-black/20 border-flux-cyan/10 mx-0 rounded-none sm:rounded-lg sm:mx-0 border-b border-t sm:border cursor-pointer"
      onClick={handleClick}
    >
      <div className="p-3 sm:p-4 space-y-3">
        <PostHeader 
          post={{
            ...post,
            profiles: {
              ...post.profiles,
              username: formatUsername(post.profiles.username)
            }
          }}
          isOwner={currentUserId === post.user_id}
          onDelete={handleDelete}
        />
        <PostContent 
          content={post.content}
          mediaUrl={post.media_url}
          mediaType={post.media_type}
          attachmentUrl={post.attachment_url}
          attachmentType={post.attachment_type}
          attachmentName={post.attachment_name}
        />
        
        {linkedStartup && <LinkedStartup startup={linkedStartup} />}
      </div>

      <PostActions 
        postId={post.id}
        onComment={() => setShowComments(!showComments)}
        initialCommentCount={commentCount}
        initialBookmarkCount={bookmarkCount}
      />
      
      {!showComments && !isDetailView && commentCount > 0 && (
        <CommentsToggle
          commentCount={commentCount}
          onClick={() => setShowComments(true)}
        />
      )}
      
      {(showComments || isDetailView) && (
        <PostComments 
          postId={post.id} 
          currentUserId={currentUserId}
          comments={post.comments || []}
          onCommentAdded={() => setCommentCount(prev => prev + 1)}
          onCommentDeleted={() => setCommentCount(prev => prev - 1)}
        />
      )}
    </Card>
  );
};
